import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { DayClickEventHandler, DayModifiers } from 'react-day-picker';
import {
  addMonths,
  differenceInDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { DateRangeInput } from './DateRangeInput';
import { DayPickerBase } from '../DayPickerBase';
import { getDatePickerLocale } from '../DatePickerLocale';
import { Caption } from '../Caption';
import { useDatePickerAttributes } from '../hooks/useDatePickerAttributes';
import { Stack } from '../../stack';
import { Button } from '../../button';
import { useProviderConfig } from '../../provider';
import { CalendarType, YearMonthCalendar } from './YearMonthCalendar';
import { checkInputValidation, convertStringToDate, convertDateToDateWithTime } from './utils';
import { DATE_FORMAT } from '../constants';
import { DatePickerDisplay } from '../DatePickerDisplay';
import { Months, MonthsProps } from './Months';
import { CaptionLabelType, Dates, DateUnitPickerProps } from '../types';

export const DateUnitPickerOld = ({
  width = '100%',
  error,
  unit = 'week',
  dates,
  disabled,
  className,
  size = 'medium',
  maxDate,
  minDate,
  showRemoveButton = false,
  numberOfMonths: numberOfMonthsProps = 1,
  autoCloseable = true,
  opened: openProp,
  displayMode = 'popover',
  weekStartsOn = 1,
  onOpen,
  onClose,
  onChange,
}: DateUnitPickerProps) => {
  const isBottomSheetPicker = displayMode === 'bottomSheet';
  const { locale } = useProviderConfig();
  const { cellSize, footer } = useDatePickerAttributes(displayMode);
  const {
    container: { style: footerStyle, ...footerProps },
  } = footer;
  const [opened, setOpened] = useState<boolean>(Boolean(openProp));
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);
  const [placeholderValue, setPlaceholderValue] = useState<string>('');
  const initialType = unit === 'month' ? 'month' : 'day';
  const [calendarType, setCalendarType] = useState<CalendarType>(initialType);
  const [inputValue, setInputValue] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [errorStatus, setErrorStatus] = useState<boolean>(Boolean(error));
  const datePickerLocale = getDatePickerLocale(locale?.lang);
  const isUnControlled = !autoCloseable || isBottomSheetPicker;
  const today = useRef<Date>(new Date()).current;

  const numberOfMonths = useMemo(() => {
    return isBottomSheetPicker ? 1 : Math.max(numberOfMonthsProps, 1);
  }, [numberOfMonthsProps, isBottomSheetPicker]);

  const initialDays = useMemo(
    () => ({
      from: dates?.from,
      to: dates?.to,
    }),
    [dates],
  );

  const mergedDisabled = useMemo<[boolean, boolean]>(() => {
    if (Array.isArray(disabled)) {
      return disabled;
    }

    return [disabled || false, disabled || false];
  }, [disabled]);

  const [currentMonth, setCurrentMonth] = useState(initialDays.to ?? today);
  const [selectedDateRange, setSelectedDateRange] = useState<Dates>(initialDays);

  const modifiers = useMemo(() => {
    return {
      start: selectedDateRange.from ? startOfDay(selectedDateRange.from) : null,
      end: selectedDateRange.to ? endOfDay(selectedDateRange.to) : null,
    };
  }, [selectedDateRange]);

  const handleInitial = (reset: boolean) => {
    if (!opened) {
      // @ts-expect-error: TS2345
      const initialDates = getInitialDate(initialDays);
      const newInputValue = initialDates.inputValue;
      const newDate = reset ? initialDates.resetDate : initialDays;
      setErrorStatus(Boolean(error));
      setInputValue({ start: newInputValue.from, end: newInputValue.to });
      setSelectedDateRange(newDate);
      // @ts-expect-error: TS2345
      setCurrentMonth(newDate.from);
      reset && onChange?.(newDate.from, newDate.to);
    }
  };

  useEffect(() => {
    setCalendarType(initialType);
  }, [unit]);

  useEffect(() => {
    if (!opened) {
      setErrorStatus(Boolean(error));
      setCalendarType(initialType);
      setPlaceholderValue('');
    } else {
      if (initialDays.from) {
        setCurrentMonth(initialDays.from);
      } else if (initialDays.to) {
        const changed_month = isBottomSheetPicker ? initialDays.to : subMonths(initialDays.to, 0);
        // @ts-expect-error: TS2345
        setCurrentMonth(isSameMonth(initialDays.from, initialDays.to) ? initialDays.from : changed_month);
      }
    }
  }, [opened]);

  useEffect(() => {
    if (modifiers.start) {
      setCurrentMonth(modifiers.start);
    } else if (modifiers.end) {
      const changed_month = isBottomSheetPicker ? modifiers.end : subMonths(modifiers.end, 0);
      // @ts-expect-error: TS2345
      setCurrentMonth(isSameMonth(modifiers.start, modifiers.end) ? modifiers.start : changed_month);
    }
  }, [modifiers]);

  useEffect(() => {
    setErrorStatus(Boolean(error));
  }, [error]);

  const getInitialDate = ({ from, to }: { from: Date; to: Date }) => {
    if (mergedDisabled[0] === false && mergedDisabled[1] === true) {
      return {
        inputValue: { from: '', to: format(to, DATE_FORMAT) },
        resetDate: { from: undefined, to: initialDays.to },
      };
    } else if (mergedDisabled[0] === true && mergedDisabled[1] === false) {
      return {
        inputValue: { from: format(from, DATE_FORMAT), to: '' },
        resetDate: { from: initialDays.from, to: undefined },
      };
    } else {
      return { inputValue: { from: '', to: '' }, resetDate: { from: undefined, to: undefined } };
    }
  };

  const handleDayClick = (day: Date, modifiers: DayModifiers): DayClickEventHandler | undefined => {
    if (modifiers.disabled) return;
    const from =
      unit === 'day'
        ? startOfDay(day)
        : minDate && isBefore(day, minDate)
        ? startOfDay(minDate)
        : startOfWeek(day, { weekStartsOn });
    const to =
      unit === 'day'
        ? endOfDay(day)
        : maxDate && isBefore(day, maxDate) && isBefore(maxDate, endOfWeek(day, { weekStartsOn }))
        ? startOfDay(maxDate)
        : endOfWeek(day, { weekStartsOn });

    setInputValue({ start: format(from, DATE_FORMAT), end: format(to, DATE_FORMAT) });
    setSelectedDateRange({ from, to });

    if (!isUnControlled) {
      onChange?.(startOfDay(from), endOfDay(to));
      setOpened(false);
    }
    return;
  };

  const disabledDays = useCallback(
    (day: Date) => {
      const currentDay = new Date(day);
      // @ts-expect-error: TS2769
      const maxRangeDay = new Date(maxDate);
      // @ts-expect-error: TS2769
      const minRangeDay = new Date(minDate);

      const maxResult = endOfDay(new Date(maxRangeDay));
      const minResult = startOfDay(new Date(minRangeDay));
      if (maxDate && minDate) {
        return isBefore(currentDay, minResult) || isAfter(currentDay, maxResult);
      }
      if (maxDate) {
        return isAfter(currentDay, maxResult);
      }
      if (minDate) {
        return isBefore(currentDay, minResult);
      }
    },
    [maxDate, minDate],
  );

  const disabledYearMonth = useCallback(
    (day: Date) => {
      const currentYear = format(startOfDay(day), 'yyyyMM');
      const maxYear = maxDate && format(maxDate, 'yyyyMM');
      const minYear = minDate && format(minDate, 'yyyyMM');
      if (maxDate && minDate && minYear && maxYear) {
        return currentYear < minYear || currentYear > maxYear;
      }
      if (maxDate && maxYear) {
        return currentYear > maxYear;
      }
      if (minDate && minYear) {
        return currentYear < minYear;
      }
    },
    [maxDate, minDate],
  );

  const handleClose = () => {
    onClose?.();
    setOpened(false);
  };

  useEffect(() => {
    if (initialDays) {
      setInputValue({
        start: initialDays.from ? format(initialDays.from, DATE_FORMAT) : '',
        end: initialDays.to ? format(initialDays.to, DATE_FORMAT) : '',
      });
      setSelectedDateRange({
        from: initialDays.from ? convertDateToDateWithTime(initialDays.from) : initialDays.from,
        to: initialDays.to ? convertDateToDateWithTime(initialDays.to) : initialDays.to,
      });
    }
  }, [initialDays, opened]);

  // numberOfMonths가 3 이상인 경우, 현재 선택된 월을 가운데에 노출하기 위해 1-2개월을 당깁니다.
  const currentMonthShifted = useMemo(() => {
    return subMonths(currentMonth ?? new Date(), Math.floor((numberOfMonths - 1) / 2));
  }, [currentMonth, numberOfMonths]);

  const renderToYearMonthCalendar = () => (
    <YearMonthCalendar
      type={calendarType}
      displayMode={displayMode}
      selectedRange={selectedDateRange}
      disabledDates={disabledYearMonth as (day: Date) => boolean}
      currentDate={currentMonth}
      locale={datePickerLocale}
      onChange={(date) => {
        if (unit === 'month') {
          // @ts-expect-error: TS2345
          const from = minDate && isBefore(date, minDate) ? startOfDay(minDate) : startOfMonth(date);
          const to =
            maxDate &&
            // @ts-expect-error: TS2345
            isBefore(date, maxDate) &&
            // @ts-expect-error: TS2345
            maxDate.getMonth() === date.getMonth() &&
            // @ts-expect-error: TS2345
            maxDate.getFullYear() === date.getFullYear()
              ? startOfDay(maxDate)
              : // @ts-expect-error: TS2345
                endOfMonth(date);
          setInputValue({
            start: format(from, DATE_FORMAT),
            end: format(to, DATE_FORMAT),
          });
          onChange?.(from, to);
        }
        // @ts-expect-error: TS2345
        setCurrentMonth(date);
      }}
      onClose={() => {
        if (unit !== 'month') {
          setCalendarType('day');
        } else {
          setOpened(false);
        }
      }}
    />
  );

  const hoveredRangeList = useMemo(() => {
    if (placeholderValue && unit === 'week') {
      const hoveredDate = convertStringToDate(placeholderValue).setHours(0, 0, 0, 0);
      return eachDayOfInterval({
        start: startOfWeek(hoveredDate, { weekStartsOn }),
        end: endOfWeek(hoveredDate, { weekStartsOn }),
      });
    } else {
      return [];
    }
  }, [placeholderValue, unit, weekStartsOn]);

  /**
   * @param value
   * @param strongCheck value empty case, value.length 가 DATE_FORMAT 보다 적은 케이스 error 판단 유무.
   */
  const checkValidation = (value: string, strongCheck = false) => {
    if (error) {
      return false;
    }

    if (!checkInputValidation(value, DATE_FORMAT, strongCheck)) {
      return false;
    }

    if (checkInputValidation(value, DATE_FORMAT, true) && disabledDays(convertStringToDate(value))) {
      return false;
    }

    return true;
  };

  const selectedRangeLabel = useMemo(() => {
    const from = checkValidation(inputValue.start, true) ? convertStringToDate(inputValue.start) : null;
    const to = checkValidation(inputValue.end, true) ? convertStringToDate(inputValue.end) : null;

    if (from && to && placeholderValue) {
      if (unit === 'week' && (differenceInDays(to, from) > 6 || getDay(from) !== weekStartsOn)) {
        return {
          from: null,
          to: null,
        };
      } else if (unit === 'day' && !isSameDay(from, to)) {
        return {
          from: null,
          to: null,
        };
      }
    }

    return {
      from: from ?? to,
      to,
    };
  }, [inputValue, selectedDateRange, checkValidation]);

  const MonthsComponent = useCallback(
    (props: MonthsProps) => <Months {...props} onTableMouseLeave={() => setPlaceholderValue('')} />,
    [],
  );

  const renderToCalendar = () => {
    return (
      <DayPickerBase
        cellSize={cellSize}
        displayMode={displayMode}
        locale={datePickerLocale}
        mode='range'
        className={classNames(unit === 'week' ? ['unit-week-calendar', 'unit-calendar'] : ['unit-calendar'], className)}
        fromMonth={minDate}
        toMonth={maxDate}
        // @ts-expect-error: TS2322
        disabled={disabledDays}
        fixedWeeks
        showOutsideDays
        numberOfMonths={numberOfMonths}
        modifiers={{
          hoverOutsideRange: hoveredRangeList,
          hoverInsideRange: hoveredRangeList,
          hoveredFirst: hoveredRangeList[0],
          hoveredLast: hoveredRangeList[hoveredRangeList.length - 1],
        }}
        modifiersClassNames={{
          hoverOutsideRange: 'unit-hover',
          hoverInsideRange: 'unit-hover-inside',
          hoveredFirst: 'unit-hover-first',
          hoveredLast: 'unit-hover-last',
        }}
        // @ts-expect-error: TS2322
        selected={selectedRangeLabel}
        month={currentMonthShifted}
        onMonthChange={(shiftedMonth) => {
          setCurrentMonth(addMonths(shiftedMonth, Math.floor((numberOfMonths - 1) / 2)));
        }}
        onDayClick={handleDayClick}
        onDayMouseEnter={(date) => {
          setPlaceholderValue(format(date, DATE_FORMAT));
        }}
        components={{
          Caption: (props) => (
            <Caption
              {...props}
              onClickCaptionLabel={(type: CaptionLabelType) => setCalendarType(type)}
              locale={datePickerLocale}
              displayMode={displayMode}
              multipleMonths={!isBottomSheetPicker && !(numberOfMonths === 1)}
            />
          ),
          // TODO: react-day-picker v9 업데이트시 MonthGrid로 대체
          Months: MonthsComponent,
        }}
        footer={
          isBottomSheetPicker &&
          unit === 'day' && (
            <Button
              fill
              kind='outlined_primary'
              onClick={() => {
                const from = startOfDay(today);
                const to = endOfDay(today);
                setSelectedDateRange({
                  from,
                  to,
                });
                onChange?.(from, to);
              }}
            >
              {locale?.DatePicker.footer.today}
            </Button>
          )
        }
      />
    );
  };

  const renderToFooter = () => {
    const {
      container: { style, ...footerProps },
    } = footer;
    return (
      <Stack gap={8} {...footerProps} css={style}>
        <Button
          onClick={() => {
            handleInitial(false);
            handleClose();
          }}
          {...footer.button}
        >
          {locale?.DatePicker.footer.clear}
        </Button>
        <Button
          kind='primary'
          disabled={!(modifiers.start && modifiers.end)}
          onClick={() => {
            if (modifiers) {
              onChange?.(modifiers.start ?? undefined, modifiers.end ?? undefined);
              handleClose();
            }
          }}
          {...footer.button}
        >
          {locale?.DatePicker.footer.ok}
        </Button>
      </Stack>
    );
  };

  const renderToInput = () => (
    <Stack
      direction='row'
      width={width}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (!opened) {
            setOpened(true);
          }
        }
      }}
      onMouseEnter={() => showRemoveButton && setShowRemoveIcon(true)}
      onMouseLeave={() => showRemoveButton && setShowRemoveIcon(false)}
      onClick={() => setOpened(true)}
    >
      <DateRangeInput
        readOnly={isBottomSheetPicker}
        disabled={mergedDisabled}
        className={className}
        status={errorStatus ? 'error' : 'info'}
        size={size}
        placeholder={placeholderValue}
        inputValue={inputValue}
        fetchInputValue={setInputValue}
        showRemoveIcon={Boolean(inputValue.start && inputValue.end && showRemoveIcon)}
        onClickRemoveButton={() => handleInitial(true)}
        modalOpen={Boolean(opened)}
      />
    </Stack>
  );

  return (
    <DatePickerDisplay
      displayMode={displayMode}
      className={className}
      disabled={mergedDisabled[0] && mergedDisabled[1]}
      input={renderToInput()}
      content={calendarType === 'day' ? renderToCalendar() : renderToYearMonthCalendar()}
      footer={(() => {
        if (displayMode === 'popover') {
          if (calendarType === 'day') {
            if (unit !== 'day' && !isUnControlled) {
              return null;
            }
            return (
              <Stack direction='row' {...footerProps} css={footerStyle} width='100%' justify='space-between'>
                {unit === 'day' && (
                  <Button
                    fill
                    kind='outlined_primary'
                    onClick={() => {
                      const from = startOfDay(today);
                      const to = endOfDay(today);
                      setSelectedDateRange({
                        from,
                        to,
                      });
                      onChange?.(from, to);
                      onClose?.();
                    }}
                  >
                    {locale?.DatePicker.footer.today}
                  </Button>
                )}
                {isUnControlled && renderToFooter()}
              </Stack>
            );
          }
          return null;
        } else {
          return renderToFooter();
        }
      })()}
      opened={opened}
      onOpen={() => {
        setOpened(true);
        onOpen?.();
      }}
      onClose={() => {
        setOpened(false);
        onClose?.();
      }}
      onDismiss={() => {
        handleClose();
      }}
    />
  );
};
